import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Link,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LinkIcon from "@mui/icons-material/Link";
import { getEvidencias } from "../../services/evidenciasService";
import "swiper/css";
import "swiper/css/navigation";

// Datos de respaldo cuando no hay evidencias desde la API
const MOCK_EVIDENCIAS = [
  {
    id: 1,
    Link: "",
    SubTitulo: "",
    Fecha: null,
    Cuerpo:
      "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla dui. Fusce feugiat malesuada odio. Morbi nunc odio, gravida at, cursus nec, luctus a, lorem.",
    Titular: "John Dow",
    Fuente: "Jefe de cocina",
    Imagen: null,
  },
  {
    id: 2,
    Link: "",
    SubTitulo: "",
    Fecha: null,
    Cuerpo:
      "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla dui. Fusce feugiat malesuada odio. Morbi nunc odio, gravida at, cursus nec, luctus a, lorem.",
    Titular: "Ricardo Goff",
    Fuente: "Especialista",
    Imagen: null,
  },
  {
    id: 3,
    Link: "",
    SubTitulo: "",
    Fecha: null,
    Cuerpo:
      "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla dui. Fusce feugiat malesuada odio. Morbi nunc odio, gravida at, cursus nec, luctus a, lorem.",
    Titular: "Jennifer Dawn",
    Fuente: "Coordinadora",
    Imagen: null,
  },
  {
    id: 4,
    Link: "",
    SubTitulo: "",
    Fecha: null,
    Cuerpo:
      "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla dui. Fusce feugiat malesuada odio. Morbi nunc odio, gravida at, cursus nec, luctus a, lorem.",
    Titular: "Paul Smith",
    Fuente: "médico",
    Imagen: null,
  },
  {
    id: 5,
    Link: "",
    SubTitulo: "",
    Fecha: null,
    Cuerpo:
      "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla dui. Fusce feugiat malesuada odio. Morbi nunc odio, gravida at, cursus nec, luctus a, lorem.",
    Titular: "Alan Christier",
    Fuente: "Consultor",
    Imagen: null,
  },
  {
    id: 6,
    Cuerpo:
      "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla dui. Fusce feugiat malesuada odio. Morbi nunc odio, gravida at, cursus nec, luctus a, lorem.",
    Titular: "Joan Coal",
    Fuente: "Directora",
    Imagen: null,
    Link: "",
    SubTitulo: "",
    Fecha: null,
  },
];

const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1557683316-973673baf926?w=800&h=600&fit=crop";

const ensureUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const v = value.trim();
  return /^https?:\/\//i.test(v) ? v : v ? `https://${v}` : "";
};

const normalizeEvidencias = (data) => {
  const raw = Array.isArray(data) ? data : data?.data ?? data?.evidencias ?? [];
  const list = Array.isArray(raw) ? raw : [];
  if (list.length === 0) return MOCK_EVIDENCIAS;
  return list
    .filter((e) => e.Estado !== 0)
    .map((e, i) => ({
      id: e.idEvidencia ?? e.id ?? i + 1,
      Cuerpo: e.Cuerpo || "",
      Titular: e.Titular || e.Fuente || "Sin nombre",
      Fuente: e.Fuente || e.SubTitulo || "",
      SubTitulo: e.SubTitulo || "",
      Fecha: e.Fecha || null,
      Imagen: e.Imagen || null,
      Link: e.Link || e.Enlace || e.Url || "",
    }));
};

const EvidenceResults = () => {
  const [evidencias, setEvidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalEvidencia, setModalEvidencia] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getEvidencias()
      .then((data) => {
        if (mounted) setEvidencias(normalizeEvidencias(data));
      })
      .catch(() => {
        if (mounted) setEvidencias(MOCK_EVIDENCIAS);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const list = evidencias;
  const listRound = list.slice(0);   // índice 0 en adelante
  const listBox = list.slice(3);    // índice 3 en adelante
  const listSimple = list.slice(6); // índice 6 en adelante
  const openModal = (item) => setModalEvidencia(item);
  const closeModal = () => setModalEvidencia(null);
  const hasLink = (item) => !!ensureUrl(item?.Link);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#fff",
      }}
    >
      {/* Hero: EVIDENCIAS & RESULTADOS + subtítulo */}
      <Box
        sx={{
          width: "100%",
          py: { xs: 4, md: 6 },
          px: 2,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.tertiary?.main || "#7c9270"} 0%, ${theme.palette.primary?.main || "#113253"} 100%)`,
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          EVIDENCIAS & RESULTADOS
        </Typography>
        <Typography variant="subtitle1" sx={{ opacity: 0.95 }}>
          subtítulo
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4, width: "100%" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
        <>
        {/* Carrusel 1: Estilo redondeado con carrusel */}
        {listRound.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "space-between", width: "100%" }}>
              <IconButton
                className="swiper-button-prev-rounded"
                size="small"
                aria-label="Anterior"
                sx={{ color: "tertiary.main" }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                className="swiper-button-next-rounded"
                size="small"
                aria-label="Siguiente"
                sx={{ color: "tertiary.main" }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
          <Swiper
            modules={[Navigation]}
            navigation={{
              prevEl: ".swiper-button-prev-rounded",
              nextEl: ".swiper-button-next-rounded",
            }}
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{
              600: { slidesPerView: 2 },
              960: { slidesPerView: 3 },
            }}
            className="evidence-swiper-rounded"
          >
            {listRound.map((item) => (
              <SwiperSlide key={item.id}>
                <Box
                  onClick={() => openModal(item)}
                  sx={{
                    p: 2.5,
                    minHeight: { xs: 260, md: 280 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fff",
                    boxShadow: 1,
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    "&:hover": { boxShadow: 2 },
                  }}
                >
                  <Typography
                    variant="body2"
                    className="evidence-card-body"
                    sx={{
                      color: "text.primary",
                      flex: 1,
                      minHeight: 0,
                      mb: 2,
                      maxHeight: "8.5em",
                    }}
                  >
                    {item.Cuerpo}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 600,
                      mb: 1.5,
                    }}
                  >
                    {item.Titular}
                  </Typography>
                  <Box
                    component="img"
                    src={item.Imagen || DEFAULT_IMAGE}
                    alt={item.Titular}
                    onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
        )}

        {/* Carrusel 2: Caja estilo carrusel */}
        {listBox.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "space-between", width: "100%" }}>
              <IconButton
                className="swiper-button-prev-box"
                size="small"
                aria-label="Anterior"
                sx={{ color: "tertiary.main" }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                className="swiper-button-next-box"
                size="small"
                aria-label="Siguiente"
                sx={{ color: "tertiary.main" }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
          <Swiper
            modules={[Navigation]}
            navigation={{
              prevEl: ".swiper-button-prev-box",
              nextEl: ".swiper-button-next-box",
            }}
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{
              600: { slidesPerView: 2 },
              960: { slidesPerView: 3 },
            }}
            className="evidence-swiper-box"
          >
            {listBox.map((item) => (
              <SwiperSlide key={`box-${item.id}`}>
                <Box
                  onClick={() => openModal(item)}
                  sx={{
                    p: 2.5,
                    minHeight: { xs: 280, md: 320 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fff",
                    boxShadow: 2,
                    cursor: "pointer",
                    position: "relative",
                    overflow: "auto",
                    "&:hover": { boxShadow: 3 },
                  }}
                >
                  <Typography
                    variant="body2"
                    className="evidence-card-body"
                    sx={{
                      color: "text.primary",
                      flex: 1,
                      minHeight: 0,
                      mb: 2,
                      maxHeight: "5.5em",
                    }}
                  >
                    {item.Cuerpo}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {item.Titular}
                  </Typography>
                  <Box
                    component="img"
                    src={item.Imagen || DEFAULT_IMAGE}
                    alt={item.Titular}
                    onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
                    sx={{
                      width: "100%",
                      maxWidth: 200,
                      height: 140,
                      borderRadius: 2,
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
        )}

        {/* Carrusel 3: Estilo sencillo con carrusel (solo texto) */}
        {listSimple.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "space-between", width: "100%" }}>
              <IconButton
                className="swiper-button-prev-simple"
                size="small"
                aria-label="Anterior"
                sx={{ color: "tertiary.main" }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                className="swiper-button-next-simple"
                size="small"
                aria-label="Siguiente"
                sx={{ color: "tertiary.main" }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
          <Swiper
            modules={[Navigation]}
            navigation={{
              prevEl: ".swiper-button-prev-simple",
              nextEl: ".swiper-button-next-simple",
            }}
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{
              600: { slidesPerView: 2 },
              960: { slidesPerView: 3 },
            }}
            className="evidence-swiper-simple"
          >
            {listSimple.map((item) => (
              <SwiperSlide key={`simple-${item.id}`}>
                <Box
                  onClick={() => openModal(item)}
                  sx={{
                    p: 2.5,
                    minHeight: { xs: 180, md: 200 },
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fafafa",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "auto",
                    "&:hover": { bgcolor: "#f0f0f0" },
                  }}
                >
                  <Typography
                    variant="body2"
                    className="evidence-card-body"
                    sx={{
                      color: "text.primary",
                      flex: 1,
                      minHeight: 0,
                      mb: 2,
                      maxHeight: "5.5em",
                    }}
                  >
                    {item.Cuerpo}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 600,
                    }}
                  >
                    – {item.Titular}
                  </Typography>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
        )}
        </>
        )}
      </Container>

      {/* Modal detalle de evidencia */}
      <Dialog
        open={!!modalEvidencia}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
            overflow: "hidden",
            bgcolor: "#fff",
            mx: { xs: 1.5, sm: 3 },
          },
        }}
      >
        {modalEvidencia && (
          <>
            <DialogTitle
              sx={{
                py: 2,
                px: 3,
                bgcolor: "#fff",
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}>
                  {modalEvidencia.Titular}
                </Typography>
                {modalEvidencia.SubTitulo && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {modalEvidencia.SubTitulo}
                  </Typography>
                )}
              </Box>
              <IconButton
                aria-label="Cerrar"
                onClick={closeModal}
                size="small"
                sx={{
                  color: "text.secondary",
                  "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
              <Box
                component="img"
                src={modalEvidencia.Imagen || DEFAULT_IMAGE}
                alt={modalEvidencia.Titular}
                onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
                sx={{
                  width: "100%",
                  maxHeight: 280,
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <Box sx={{ px: 3, py: 2.5 }}>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.7,
                    color: "text.primary",
                  }}
                >
                  {modalEvidencia.Cuerpo}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "#fafafa",
                }}
              >
                {modalEvidencia.Fecha && (
                  <Chip
                    label={modalEvidencia.Fecha}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 1,
                      borderColor: "divider",
                      color: "text.secondary",
                      fontWeight: 500,
                    }}
                  />
                )}
                {hasLink(modalEvidencia) && (
                  <Link
                    href={ensureUrl(modalEvidencia.Link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    <LinkIcon fontSize="small" />
                    Ver enlace
                  </Link>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default EvidenceResults;
